/**
 * Offset pagination arithmetic, kept away from the components that render it.
 *
 * The API returns `total` alongside the page (spec R6.8), which is everything
 * needed to say "25–48 of 57" and to know whether the arrows are live. The one
 * rule worth stating: a page beyond the end is not an error, it is an empty
 * page, and the caller is expected to offer a way back rather than to redirect.
 */

export interface PageRange {
  /** 1-based index of the first item shown, or 0 when there are none. */
  from: number;
  /** 1-based index of the last item shown, or 0 when there are none. */
  to: number;
  /** Total pages. At least 1, so "Page 1 of 1" is always sayable. */
  pageCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  /** True when the requested page sits past the last one with rows on it. */
  isBeyondEnd: boolean;
}

export const getPageRange = (
  page: number,
  pageSize: number,
  total: number,
): PageRange => {
  const safePageSize = Math.max(1, Math.floor(pageSize));
  const safeTotal = Math.max(0, Math.floor(total));
  const safePage = Math.max(1, Math.floor(page));

  const pageCount = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const offset = (safePage - 1) * safePageSize;

  /* A page past the end shows nothing, so it spans nothing: 0 to 0, not 49–57. */
  const isEmptyPage = safeTotal === 0 || offset >= safeTotal;
  const from = isEmptyPage ? 0 : offset + 1;
  const to = isEmptyPage ? 0 : Math.min(safeTotal, safePage * safePageSize);

  return {
    from,
    to,
    pageCount,
    hasPrevious: safePage > 1,
    hasNext: safePage < pageCount,
    isBeyondEnd: safeTotal > 0 && safePage > pageCount,
  };
};
